import type { FC } from "react";
import React, { useEffect, useMemo, useState } from "react";
import type { FileHeaderMetadata } from "src/state/FilesCubit/FilesCubit";
import { fileState } from "src/state/state";
import FilePreview from "src/ui/components/FilePreview/FilePreview";
import type { ChatMessage } from "src/ui/components/Chat/ChatBloc";
import translate from "src/lib/translate";

interface SimpleFilePreviewProps {
  name: string;
  type: string;
  id: string;
  size?: number;
}

const DisplayFileAttachmentsFileList: FC<{
  message: ChatMessage;
}> = ({ message }) => {
  const [fileMetadata, setFileMetadata] = useState<
    Record<string, FileHeaderMetadata | undefined>
  >({});

  const fileTypeName = useMemo(() => {
    if (message.computed.isLabReportUpload) {
      return translate("type_name_lab_report_upload");
    }
    return translate("type_name_file_unknown");
  }, [message.computed]);

  const fileIds = message.computed.filesIdList ?? [];

  useEffect(() => {
    if (!message.computed.isLabReportUpload || fileIds.length === 0) return;

    const fetchFileMetadata = async () => {
      const metadata: Record<string, FileHeaderMetadata> = {};
      await Promise.all(
        fileIds.map(async (fileId) => {
          const result = await fileState.getFileMetadata(fileId);
          if (result) {
            metadata[fileId] = result;
          }
        })
      );
      setFileMetadata(metadata);
    };

    void fetchFileMetadata();
  }, [fileIds.join(","), message.computed.isLabReportUpload]);

  const filePreviews = useMemo<SimpleFilePreviewProps[]>(() => {
    const list: SimpleFilePreviewProps[] = [];

    for (const fileId of fileIds) {
      const metadata = fileMetadata[fileId];
      const type = metadata?.contentType ?? "";
      const size = metadata?.contentLength ?? undefined;
      let name = fileTypeName;
      if (message.computed.isLabReportUpload && fileIds.length > 1) {
        name = `${fileTypeName} - Page ${fileIds.indexOf(fileId) + 1}`;
      }
      list.push({
        name,
        type,
        id: fileId,
        size
      });
    }

    return list;
  }, [fileIds, fileTypeName, fileMetadata, message.computed.isLabReportUpload]);

  return (
    <>
      {filePreviews.map((file) => (
        <FilePreview key={file.id} id={file.id} name={file.name} />
      ))}

      {message.computed.fileAttachments.map((file) => (
        <FilePreview key={file.source} id={file.source} name={file.name} />
      ))}
    </>
  );
};

export default DisplayFileAttachmentsFileList;
